import { AMPLITUDE_FEEDBACK_SURVEY_STEPS } from '@/modules/core/amplitude/events';
import UniversalCookiesManager from '@/modules/core/cookiesManager/UniversalCookiesManager';
import { CustomerTheme } from '@/modules/core/data/types/CustomerTheme';
import { UserSemiPersistentSessionChatbotFeedback } from '@/modules/core/userSession/types/UserSemiPersistentSession';
import { shouldUpdateGrade } from '@/modules/solutionsSurvey/feedbackSurvey';
import {
  css,
  useTheme,
} from '@emotion/react';
import classnames from 'classnames';
import React, {
  Dispatch,
  SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import SmileyBad from './SmileyBad';
import SmileyGood from './SmileyGood';
import SmileyOkay from './SmileyOkay';
import SmileyPerfect from './SmileyPerfect';
import SmileyTerrible from './SmileyTerrible';

type Props = {
  universalCookiesManager: UniversalCookiesManager;
  previousSessionChatbotFeedback: UserSemiPersistentSessionChatbotFeedback;
  currentSessionGrade: number;
  setStep: Dispatch<SetStateAction<AMPLITUDE_FEEDBACK_SURVEY_STEPS>>;
  setCurrentGrade: Dispatch<SetStateAction<number>>;
}

/**
 * Displays a list of smileys to the end-user.
 *
 * Each smiley is clickable and is assigned to a grade.
 * Upon click on a smiley, the grade will be mutated.
 */
const FeedbackSurveyRatings: React.FunctionComponent<Props> = (props) => {
  const {
    universalCookiesManager,
    previousSessionChatbotFeedback,
    currentSessionGrade,
    setStep,
    setCurrentGrade,
  } = props;
  const { primaryColor }: CustomerTheme = useTheme();
  const { t } = useTranslation();
  const previousSessionGrade = previousSessionChatbotFeedback?.grade;
  const activeSmiley: number = !currentSessionGrade ? previousSessionGrade : currentSessionGrade;

  const handleGradeChange = (grade: number): void => {
    // Don't send any event if the user is selecting the same grade as the one already selected
    if (shouldUpdateGrade(previousSessionGrade, currentSessionGrade, grade)) {
      // Store grade in cookie
      universalCookiesManager.patchUserData({
        chatbotFeedback: {
          ...previousSessionChatbotFeedback,
          grade,
        },
      });
    }

    setCurrentGrade(grade);
    setStep(AMPLITUDE_FEEDBACK_SURVEY_STEPS.ASK_COMMENT);
  };

  return (
    <div
      className={'ratings'}
      css={css`
        display: inline-flex;
        place-content: space-between;
        width: 50%;
        margin-top: 30px;
        height: 70px; // Avoids accordion effect when hovering (must be greater than SVG height)

        @media (max-width: 991.98px) {
          width: 100%;
          font-size: 12px;
          line-height: 14px;
        }

        .icon {
          background-color: transparent;
          border: none;

          &.active,
          &:hover {
            svg {
              fill: ${primaryColor};
            }
          }
        }

        svg {
          width: 40px;
          transition: width 300ms, fill 300ms, margin-bottom 300ms, transform 300ms;
          fill: #BEBEBE;

          &:hover {
            width: 50px;
            margin: 0px 0px 10px 0px;
          }
        }
      `}
    >
      <button
        className={classnames('icon', { active: activeSmiley === 1 })}
        onClick={(): void => {
          handleGradeChange(1);
        }}
      >
        <SmileyTerrible />
        <br />
        <span>
          {t('solutionsPage.feedbackSurvey.grade1', `Pas du tout`)}
        </span>
      </button>

      <button
        className={classnames('icon', { active: activeSmiley === 2 })}
        onClick={(): void => {
          handleGradeChange(2);
        }}
      >
        <SmileyBad />
        <br />
        <span>
          {t('solutionsPage.feedbackSurvey.grade2', `Pas trop`)}
        </span>
      </button>

      <button
        className={classnames('icon', { active: activeSmiley === 3 })}
        onClick={(): void => {
          handleGradeChange(3);
        }}
      >
        <SmileyOkay />
        <br />
        <span>
          {t('solutionsPage.feedbackSurvey.grade3', `Pas d'avis`)}
        </span>
      </button>

      <button
        className={classnames('icon', { active: activeSmiley === 4 })}
        onClick={(): void => {
          handleGradeChange(4);
        }}
      >
        <SmileyGood />
        <br />
        <span>
          {t('solutionsPage.feedbackSurvey.grade4', `Plutôt utile`)}
        </span>
      </button>

      <button
        className={classnames('icon', { active: activeSmiley === 5 })}
        onClick={(): void => {
          handleGradeChange(5);
        }}
      >
        <SmileyPerfect />
        <br />
        <span>
          {t('solutionsPage.feedbackSurvey.grade5', `Très utile`)}
        </span>
      </button>
    </div>
  );
};

export default FeedbackSurveyRatings;
